import { useEffect, useState } from 'preact/hooks';
import { api } from '../api.js';
import { navigate, listsVersion } from '../store.js';
import { __, _n } from '../i18n.js';
import { useVisibleRefresh } from '../lib/refresh.js';
import Icon from '../components/Icon.jsx';

/**
 * HH-7 dashboard card: the Boodschappen badge — how many items are still
 * unchecked, tapping through to the Boodschappen tab (the brief's card 3).
 *
 * Reads the existing GET /lists, whose rows already carry unchecked_count
 * (HH-4, corrected in HH-5's review so an itemless list no longer counts
 * as one). Summing those numbers is arithmetic over an answer the server
 * already gave, not a second opinion about what "unchecked" means.
 *
 * Vaste lijsten are excluded: a template is a starting point you pour
 * from, never something you are currently shopping (the locked decision
 * that a list is the instrument of one doable task). Hidden entirely when
 * the fetch fails or nothing is open — a "0 items" badge is noise.
 */
export default function ShoppingCard() {
  const [lists, setLists] = useState(null);
  const version = listsVersion.value; // refresh after any list/item mutation
  // ...and whenever the app returns to the foreground: the server froze
  // today's date into this payload when it was built.
  const visibleTick = useVisibleRefresh();

  useEffect(() => {
    let cancelled = false;

    api.lists
      .list()
      .then((res) => {
        if (!cancelled) setLists(res.lists || []);
      })
      .catch(() => {
        if (!cancelled) setLists(null);
      });

    return () => {
      cancelled = true;
    };
  }, [version, visibleTick]);

  if (!lists) return null;

  const live = lists.filter((list) => !list.is_template);
  const unchecked = live.reduce((total, list) => total + (Number(list.unchecked_count) || 0), 0);
  if (unchecked === 0) return null;

  // One open list is the common case and deserves its own name; several
  // is a count, because naming three lists on a dashboard is a wall.
  const withItems = live.filter((list) => Number(list.unchecked_count) > 0);
  const subtitle =
    withItems.length === 1
      ? withItems[0].name
      : _n('%d lijst', '%d lijsten', withItems.length, 'etm').replace('%d', withItems.length);

  return (
    <section class="etm-dashboard-card">
      <button
        type="button"
        class="etm-dashboard-card__header"
        onClick={() => navigate(withItems.length === 1 ? 'listDetail' : 'lists', withItems.length === 1 ? { listId: withItems[0].id } : {})}
      >
        <span class="etm-dashboard-card__icon">
          <Icon name="shopping_cart" size={20} />
        </span>
        <span class="etm-dashboard-card__title">{__('Boodschappen', 'etm')}</span>
        <span class="etm-dashboard-card__badge">{unchecked}</span>
        <Icon name="chevron_right" size={20} />
      </button>
      <p class="etm-dashboard-card__empty">
        {_n('%d ding op je lijst', '%d dingen op je lijst', unchecked, 'etm').replace('%d', unchecked)}
        {' · '}
        {subtitle}
      </p>
    </section>
  );
}
